/* eslint-disable no-param-reassign */
import { Typography, Button, Form, Input, Radio, InputNumber, message, Col, Row } from 'antd';
import { ReactComponent as UploadSVG } from 'assets/images/Upload.svg';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Dragger from 'antd/lib/upload/Dragger';
import { beforeImage5mbUpload } from 'utils';
import { FC, useEffect, useState } from 'react';
import { TVerifyPatientTokenResponse, useSubmitTreatmentCheckinMutation } from 'services/patientsApi';
import { s3Upload } from 'services/s3';
import { useGetProfileSignedUrlMutation } from 'modules/auth/services/authApi';
import DeleteIcon from 'components/DeleteIcon';
import { formatSaveImage, onFormValuesChange } from 'helpers';
import { notificationDefaultError, ZINDEX } from 'utils/ui';
import './submit-treatment-check-ins-form.less';

const { TextArea } = Input;
const { Text } = Typography;

type TProps = {
  token: string;
  checkInNumber: string;
  checkInData: TVerifyPatientTokenResponse;
};

export const SubmitTreatmentCheckInForm: FC<TProps> = ({ token, checkInNumber, checkInData }) => {
  const [form] = Form.useForm();
  const [submitTreatmentCheckin, { isLoading }] = useSubmitTreatmentCheckinMutation();
  const history = useHistory();
  const [images, setImages]: any = useState([]);
  const [getProfileSignedUrl] = useGetProfileSignedUrlMutation();
  const [formData, setFormData]: any = useState();

  const onFinish = async (values: any) => {
    const reqData = {
      ...values,
      alignerStage: parseInt(values.alignerStage, 10),
      checkInNumber: parseInt(values.checkInNumber, 10),
      wearingDuration: parseInt(values.wearingDuration, 10),
      havingConcerns: values.havingConcerns === 'true',
      images: formatSaveImage(images),
    };
    try {
      const res: any = await submitTreatmentCheckin(reqData);
      if (res && res.data && res.data.id) {
        history.push('/patients/check-in/success', { replace: true });
      } else {
        notificationDefaultError();
      }
    } catch (e) {
      notificationDefaultError();
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      password: token,
      checkInNumber,
      checkInId: checkInData.id,
    });
  }, []);

  const onFinishFailed = ({ errorFields }: any) => {
    form.scrollToField(errorFields[0].name, { block: 'center' });
  };

  const uploadImageRequest = async ({ file, onSuccess, filename }: any) => {
    const { name, uid, type } = file;
    const supportedFileType = type === 'image/jpeg' || type === 'image/png' || type === 'image/jpg';
    if (!supportedFileType) {
      return false;
    }
    setImages((values: any) => [
      ...values,
      {
        uid,
        name,
        filename,
        url: null,
        status: 'uploading',
      },
    ]);

    if (file) {
      try {
        const fileData = {
          fileName: name,
          mimeType: type,
          folderName: filename,
        };
        const signedUrl = await getProfileSignedUrl(fileData).unwrap();
        await s3Upload(signedUrl.url, file);
        const imageUrl = signedUrl && signedUrl.url.split('?')[0];
        onSuccess({ imageUrl });
      } catch (e) {
        setImages((values: any) => values.filter((f: any) => f.uid !== uid));
        message.error(`Sorry ${file.name} didn't upload, please try again.`);
        throw new Error(`Upload profile pic error: ${e}`);
      }
    }
    return true;
  };

  const handleFileChange = (info: any) => {
    const { status, response, uid, type } = info.file;

    const supportedFileType = type === 'image/jpeg' || type === 'image/png' || type === 'image/jpg';
    if (!supportedFileType) {
      return false;
    }

    if (status === 'done') {
      const { imageUrl } = response;

      const copyPrevVal = [...images];

      const updateFileStatus = copyPrevVal.map((item: any) => {
        if (item.uid === uid) {
          item.status = 'done';
          item.url = imageUrl;
        }
        return item;
      });

      message.success(`${info.file.name} file uploaded successfully.`);
      setImages([...updateFileStatus]);
    } else if (status === 'error') {
      message.error(`Sorry ${info.file.name} didn't upload, please try again.`);
    }
    return true;
  };

  const handleFormValueChanges = (values: any) => {
    const dataToUpdate = onFormValuesChange(values, formData);
    setFormData(dataToUpdate);
  };

  const deleteImage = (url: string) => {
    const filterImages = images.filter((item: any) => item.url !== url);
    setImages(filterImages);
  };

  return (
    <div className="check-in-container p-4 mt-4">
      <div className="text-lg text-center font-bold mb-3" style={{ color: '#050a46' }}>
        Time to Check in!
      </div>
      <div className="pb-2 mb-2">
        <div className="text-sm">
          <strong>Clear Aligner Treatment Check in </strong>
        </div>
        <div>
          <Text className="text-body">{checkInData.patient?.fullName}</Text>
        </div>
      </div>
      <div className="border-top-1 border-gray-100 pt-1">
        <Form
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          onValuesChange={handleFormValueChanges}
          layout="vertical"
          size="large"
          className="mt-3"
        >
          <div className="grid mb-4">
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0">
              <div className="flex flex-column">
                <Text className="text-head">Name</Text>
                <Text className="text-body">{checkInData.patient?.fullName}</Text>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0">
              <div className="flex flex-column">
                <Text className="text-head">Date</Text>
                <Text className="text-body">{moment().format('DD/MM/YYYY')}</Text>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0">
              <div className="flex flex-column">
                <Text className="text-head">Aligner stage*</Text>
                <Form.Item
                  name="alignerStage"
                  rules={[
                    {
                      required: true,
                      message: 'Aligner stage is required',
                    },
                  ]}
                >
                  <InputNumber style={{ marginTop: 10, width: '100%' }} />
                </Form.Item>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0">
              <div className="flex flex-column">
                <Text className="text-head">How many days have you been wearing this aligner?*</Text>
                <Form.Item
                  name="wearingDuration"
                  rules={[
                    {
                      required: true,
                      message: 'Wearing duration is required',
                    },
                  ]}
                >
                  <InputNumber style={{ marginTop: 10, width: '100%' }} />
                </Form.Item>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0 treatment-checkins-radio-btn">
              <div className="flex flex-column">
                <Text className="text-head">Do you have any questions or concerns?*</Text>
                <Form.Item
                  name="havingConcerns"
                  className="mt-2"
                  rules={[
                    {
                      required: true,
                      message: 'This field is required',
                    },
                  ]}
                >
                  <Radio.Group>
                    <Radio.Button value="true" className="text-center">
                      Yes
                    </Radio.Button>
                    <Radio.Button value="false" className="text-center">
                      No
                    </Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0">
              <div className="flex flex-column">
                <Text className="text-head">
                  {formData?.havingConcerns === 'true'
                    ? 'Please detail your concerns here*'
                    : 'My concerns or questions'}
                </Text>
                <Form.Item
                  name="concerns"
                  className="mt-2"
                  rules={[
                    {
                      required: formData?.havingConcerns === 'true',
                      message: 'Please detail your concerns before submitting',
                    },
                  ]}
                >
                  <TextArea rows={3} />
                </Form.Item>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0 hidden">
              <div className="flex flex-column">
                <Text className="text-head">Password</Text>
                <Form.Item name="password" className="mt-2">
                  <Input />
                </Form.Item>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0 hidden">
              <div className="flex flex-column">
                <Text className="text-head">Check-in number</Text>
                <Form.Item name="checkInNumber" className="mt-2">
                  <InputNumber />
                </Form.Item>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-6 xs:pt-0 hidden">
              <div className="flex flex-column">
                <Text className="text-head">check-in Id</Text>
                <Form.Item name="checkInId" className="mt-2">
                  <Input />
                </Form.Item>
              </div>
            </div>
            <div className="col-12 sm:col-12 md:col-12 xs:pt-0">
              <div className="flex flex-column">
                <Text className="text-head">My photo update</Text>
                <div className="mt-2">
                  <span>
                    Please upload at least 3 photos whilst wearing your aligners and biting down. Please include:
                  </span>
                  <ul>
                    <li>Left</li>
                    <li>Front</li>
                    <li>Right</li>
                  </ul>
                  <span>
                    If you have any concerns or questions please try and capture close ups of the relevant area or
                    aligner in addition.
                  </span>
                </div>
              </div>
            </div>
            <div className="col-12 xs:pt-0 grid mb-4">
              <div className="col-12 md:col-6">
                <Dragger
                  name="checkInImages"
                  multiple
                  showUploadList={false}
                  customRequest={uploadImageRequest}
                  beforeUpload={beforeImage5mbUpload}
                  onChange={handleFileChange}
                >
                  <div className="flex p-2">
                    <div className="mr-4 ml-2">
                      <p className="ant-upload-drag-icon m-0">
                        <UploadSVG height={30} width={30} />
                      </p>
                    </div>
                    <div className="flex align-items-center">
                      <p className="upload-text text-left">Click or drag file to this area to upload</p>
                    </div>
                  </div>
                </Dragger>
              </div>
            </div>
            <div className="col-12 mt-4 mb-6">
              <Row gutter={24} className="mb-4">
                {images.map(({ url, status }: any) => (
                  <Col className="gutter-row relative" xs={24} md={8}>
                    {status && status !== 'done' ? (
                      <div
                        className="min-w-full flex justify-content-center align-items-center mb-2"
                        style={{ minHeight: 120, backgroundColor: '#f5f5f5' }}
                      >
                        <Typography.Text> Uploading... </Typography.Text>
                      </div>
                    ) : (
                      <div className="small-image-container mb-2 cursor-pointer w-full" style={{ minHeight: 120 }}>
                        <img src={url} className="h-full" alt="" />
                      </div>
                    )}
                    <div className="absolute right-0 -mt-1 z-4 cursor-pointer" style={{ top: -10 }}>
                      <DeleteIcon handleDelete={deleteImage} item={url} size={22} closeIcon />
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          </div>
          <div
            className="fixed bottom-0 bg-white border-top-1 border-gray-100 w-screen left-0"
            style={{ zIndex: ZINDEX.SUBMIT_TREATMENT_CHECKIN }}
          >
            <div
              className="flex flex-row justify-content-end align-items-center w-full px-4 py-2"
              style={{ maxWidth: 1000 }}
            >
              <div className="grid w-full">
                <div className="col-12">
                  <div className="flex flex-row justify-content-end align-items-center">
                    <Button type="primary" htmlType="submit" className="px-6" loading={isLoading}>
                      SUBMIT
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};
